import { useCallback } from 'react';
import * as ViewModel from '@/core/presenter/viewModel';
import { useStores } from '@/stores';
import { removeSignPrefix, removeSymbol, truncateString } from '@/utils';
import clsx from 'clsx';
import { useNavigate } from 'react-router-dom';
import { RouterPath } from '@/routes/RouterPath';
import { HomeViewType } from '@/core/presenter/viewModel';
import MemoArea from '@/components/atoms/MemoArea';
import { tryFetchBalance } from '@/core/presenter/controller/HomeController';

interface IProps {
  trxTransaction: ViewModel.IResponseActivity;
  onCancel: () => void;
}

const TrxTransactionPopup = ({ trxTransaction, onCancel }: IProps) => {
  const { userStore } = useStores();
  const myTrxAddress = truncateString(userStore.user?.trxAddress ?? '');
  const navigate = useNavigate();

  const onClickTrxAddressCopyBtn = useCallback((text: string) => {
    navigator.clipboard.writeText(text);
  }, []);

  const onClickCopyBtn = useCallback(() => {
    navigator.clipboard.writeText(trxTransaction.txID);
  }, [trxTransaction.txID]);

  const onClickTrxExplorerBtn = useCallback(() => {
    window.open(`${import.meta.env.VITE_TRON_EXPLORER_URL}#/transaction/${trxTransaction.txID}`, '_blank');
  }, [trxTransaction.txID]);

  const onClickResendBtn = useCallback(async () => {
    if (userStore.user?.trxAddress) {
      const isSuccess = await tryFetchBalance(userStore.user.trxAddress);
      if (isSuccess) {
        navigate(RouterPath.HOME, {
          state: {
            viewType: HomeViewType.SEND,
            sendData: {
              address: trxTransaction.type === '보내기' ? trxTransaction.to : trxTransaction.from,
            },
          },
        });
      }
    }
    onCancel();
  }, [userStore.user?.trxAddress, trxTransaction.type, trxTransaction.to, trxTransaction.from, onCancel, navigate]);

  return (
    <div className="modal" id="modal" style={{ display: 'flex' }}>
      <div className="modal-content">
        <div className="modal-body">
          <div className="inner">
            <div className="modal-header">
              <div className="close" onClick={onCancel}>
                <i className="bi bi-x-lg"></i>
              </div>
              <h3 className="title">{trxTransaction.type} 상세내역</h3>
            </div>
            <div className="top detail-asset">
              <div className="left">
                <h3>상태</h3>
                <span
                  className={clsx('badge', {
                    'red-badge': trxTransaction.status === ViewModel.TransactionStatus.FAIL,
                  })}
                >
                  {trxTransaction.status}
                </span>
              </div>

              <div className="right">
                <span>
                  블록탐색기에서 보기 <i className="bi bi-search" onClick={onClickTrxExplorerBtn} />
                </span>
                <span>
                  트랜젝션 ID 복사 <i className="bi bi-copy" onClick={onClickCopyBtn} />
                </span>
              </div>
            </div>
            <div className="mid">
              <div className="sender">
                <h3>보낸 사람</h3>
              </div>
              <div className="receiver">
                <h3>받은 사람</h3>
              </div>
            </div>
            <MemoArea
              myTrxAddress={myTrxAddress}
              transaction={trxTransaction}
              onClickResendBtn={onClickResendBtn}
              onClickTrxAddressCopyBtn={onClickTrxAddressCopyBtn}
            />
            <div className="bot asset-bot">
              <h3>트랜젝션</h3>
              <div className="container">
                <p>금액</p>
                <div className="right">
                  <p
                    className={clsx({
                      increase_color: trxTransaction.type === '받기',
                      decrease_color: trxTransaction.type === '보내기',
                    })}
                  >
                    {removeSymbol(removeSignPrefix(trxTransaction.amount))}
                  </p>
                  <p>{trxTransaction.coinType}</p>
                  <span>{trxTransaction.krwAmount}</span>
                </div>
              </div>
              {trxTransaction.type === '보내기' && (
                <div className="transaction-fee" style={{ display: 'flex' }}>
                  <p className="fee-label">수수료</p>
                  <p className="fee-value">{trxTransaction.fee} TRX</p>
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <button className="confirm-btn solid" onClick={onCancel}>
            확인
          </button>
        </div>
      </div>
    </div>
  );
};

export default TrxTransactionPopup;
